import { Component, inject, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { SiteService } from 'src/app/services/site.service';
import { CommonModule } from '@angular/common';
import { UtilService } from 'src/app/services/util.service';
import { CommonAPIService } from 'src/app/services/common.service';
import { Subscription } from 'rxjs';

interface Point {
  x: number;
  y: number;
}

interface Zone {
  id: string;
  name: string;
  coordinates: Point[];
  color?: string;
}

@Component({
  selector: 'app-zone-create-model',
  imports: [FormsModule, CommonModule],
  templateUrl: './zone-create-model.component.html',
  styleUrl: './zone-create-model.component.scss'
})
export class ZoneCreateModelComponent implements OnInit, AfterViewInit, OnDestroy {

  // Rendered inside the shared <sw-drawer> (app.component). When embedded the
  // panel hides its own header and closes via SiteService instead of BsModalRef.
  @Input() embedded: boolean = false;

  private modalService = inject(BsModalService);
  private siteService = inject(SiteService);
  private utilService = inject(UtilService);
  private commonService = inject(CommonAPIService);
  private cdr = inject(ChangeDetectorRef);
  // Optional: only present when shown via the legacy ngx-bootstrap modal path.
  private modalRef: BsModalRef | null = inject(BsModalRef, { optional: true });

  saveZonesSubscription: Subscription;
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
  cameraImage: string = this.siteService.cameraSiteID.alert_image || 'assets/images/zone.png';
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  imageElement: HTMLImageElement;
  imageLoaded: boolean = false;
  imageWidth: number = 0;
  imageHeight: number = 0;
  canvasWidth: number = 0;
  canvasHeight: number = 0;
  polygonPoints: Point[] = [];
  isDrawing: boolean = false;
  isEditing: boolean = false;
  selectedVertexIndex: number = -1;
  isDragging: boolean = false;
  dragOffset: Point = { x: 0, y: 0 };
  zones: Zone[] = [];
  selectedZoneId: string | null = null;
  currentZone: Zone | null = null;
  zoneName: string = '';
  undoStack: Zone[][] = [];
  redoStack: Zone[][] = [];
  maxHistorySize: number = 50;

  readonly VERTEX_RADIUS = 4;
  readonly POLYGON_BORDER_COLOR = '#FFD700';
  readonly POLYGON_FILL_COLOR = 'rgba(0, 0, 0, 0.35)';
  readonly VERTEX_COLOR = '#FF8C00';

  ngOnInit(): void {
    this.loadStaticImage();
    this.fetchZones();
    this.keyDownHandler = this.handleKeyDown.bind(this);
    document.addEventListener('keydown', this.keyDownHandler);
  }

  handleKeyDown(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      this.undo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      this.redo();
      return;
    }
    if (e.key === 'Enter' && this.isDrawing && this.polygonPoints.length >= 3) {
      e.preventDefault();
      this.saveCurrentZone();
    }
  }

  loadStaticImage(): void {
    this.imageLoaded = false;
    this.polygonPoints = [];
    this.loadImage();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeCanvas();
    }, 200);
  }

  loadImage(): void {
    this.imageElement = new Image();
    this.imageElement.crossOrigin = 'anonymous';
    this.imageElement.onload = () => {
      this.imageWidth = this.imageElement.width;
      this.imageHeight = this.imageElement.height;
      this.calculateCanvasSize();
      this.imageLoaded = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.initializeCanvas();
        this.drawCanvas();
        this.fetchZones();
      }, 100);
    };
    this.imageElement.onerror = () => {
      this.cameraImage = 'assets/images/logo.svg';
      this.loadImage();
    };
    this.imageElement.src = this.cameraImage;
  }

  calculateCanvasSize(): void {
    const maxWidth = 800;
    const maxHeight = 600;
    const aspectRatio = this.imageWidth / this.imageHeight;

    if (this.imageWidth > maxWidth || this.imageHeight > maxHeight) {
      if (aspectRatio > 1) {
        this.canvasWidth = maxWidth;
        this.canvasHeight = maxWidth / aspectRatio;
      } else {
        this.canvasHeight = maxHeight;
        this.canvasWidth = maxHeight * aspectRatio;
      }
    } else {
      this.canvasWidth = this.imageWidth;
      this.canvasHeight = this.imageHeight;
    }
  }

  initializeCanvas(): void {
    this.canvas = document.getElementById('zone-canvas') as HTMLCanvasElement;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d')!;
      this.setupCanvasEvents();
      if (this.imageLoaded && this.imageElement) {
        this.drawCanvas();
      }
    }
  }

  setupCanvasEvents(): void {
    if (!this.canvas) return;
    this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.canvas.addEventListener('mouseup', (e) => this.onMouseUp(e));
    this.canvas.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
    this.canvas.addEventListener('dblclick', (e) => this.onDoubleClick(e));
  }

  getCanvasCoordinates(e: MouseEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvasWidth / rect.width;
    const scaleY = this.canvasHeight / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }

  getVertexAtPoint(point: Point): number {
    for (let i = 0; i < this.polygonPoints.length; i++) {
      const vertex = this.polygonPoints[i];
      const distance = Math.sqrt(
        Math.pow(point.x - vertex.x, 2) + Math.pow(point.y - vertex.y, 2)
      );
      if (distance <= this.VERTEX_RADIUS * 2) {
        return i;
      }
    }
    return -1;
  }

  onMouseDown(e: MouseEvent): void {
    const point = this.getCanvasCoordinates(e);
    if (this.isEditing) {
      const vertexIndex = this.getVertexAtPoint(point);
      if (vertexIndex >= 0) {
        this.selectedVertexIndex = vertexIndex;
        this.isDragging = true;
        this.dragOffset = {
          x: point.x - this.polygonPoints[vertexIndex].x,
          y: point.y - this.polygonPoints[vertexIndex].y
        };
        this.canvas.style.cursor = 'grabbing';
        return;
      }
    }

    if (!this.isDrawing && this.polygonPoints.length === 0) {
      this.isDrawing = true;
      this.polygonPoints.push(point);
      this.drawCanvas();
    } else if (this.isDrawing) {
      const firstPoint = this.polygonPoints[0];
      const distance = Math.sqrt(
        Math.pow(point.x - firstPoint.x, 2) + Math.pow(point.y - firstPoint.y, 2)
      );
      if (distance <= this.VERTEX_RADIUS * 4 && this.polygonPoints.length >= 3) {
        e.preventDefault();
        this.saveCurrentZone();
      } else {
        this.polygonPoints.push(point);
        this.drawCanvas();
      }
    }
  }

  onMouseMove(e: MouseEvent): void {
    const point = this.getCanvasCoordinates(e);
    if (this.isDragging && this.selectedVertexIndex >= 0) {
      this.polygonPoints[this.selectedVertexIndex] = {
        x: point.x - this.dragOffset.x,
        y: point.y - this.dragOffset.y
      };
      this.drawCanvas();
      return;
    }
    if (this.isEditing) {
      const vertexIndex = this.getVertexAtPoint(point);
      if (vertexIndex >= 0) {
        this.canvas.style.cursor = 'grab';
      } else {
        this.canvas.style.cursor = 'default';
      }
    } else if (this.isDrawing) {
      this.drawCanvas();
      this.drawPreviewLine(point);
    }
  }

  onMouseUp(e: MouseEvent): void {
    if (this.isDragging) {
      this.isDragging = false;
      this.selectedVertexIndex = -1;
      this.canvas.style.cursor = 'default';
    }
  }

  onMouseLeave(e: MouseEvent): void {
    if (this.isDragging) {
      this.isDragging = false;
      this.selectedVertexIndex = -1;
      this.canvas.style.cursor = 'default';
    }
  }

  onDoubleClick(e: MouseEvent): void {
    e.preventDefault();
    if (this.isDrawing && this.polygonPoints.length >= 3) {
      this.saveCurrentZone();
    }
  }

  drawPreviewLine(point: Point): void {
    if (this.polygonPoints.length > 0) {
      const lastPoint = this.polygonPoints[this.polygonPoints.length - 1];
      this.ctx.beginPath();
      this.ctx.moveTo(lastPoint.x, lastPoint.y);
      this.ctx.lineTo(point.x, point.y);
      this.ctx.strokeStyle = this.POLYGON_BORDER_COLOR;
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([5, 5]);
      this.ctx.stroke();
      this.ctx.setLineDash([]);
    }
  }

  drawCanvas(): void {
    if (!this.ctx || !this.imageElement) return;
    this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.ctx.drawImage(this.imageElement, 0, 0, this.canvasWidth, this.canvasHeight);
    this.zones.forEach(zone => {
      if (zone.coordinates && zone.coordinates.length > 0) {
        this.drawZonePolygon(zone);
      }
    });
    if (this.polygonPoints.length > 0) {
      this.drawPolygon();
      this.drawVertices();
    }
  }

  drawZonePolygon(zone: Zone): void {
    if (!zone.coordinates || zone.coordinates.length < 2) return;
    const points = zone.coordinates.map(p => ({
      x: p.x * this.canvasWidth,
      y: p.y * this.canvasHeight
    }));
    this.ctx.beginPath();
    this.ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(points[i].x, points[i].y);
    }
    if (points.length >= 3) {
      this.ctx.closePath();
      this.ctx.fillStyle = this.POLYGON_FILL_COLOR;
      this.ctx.fill();
    }
    this.ctx.strokeStyle = zone.color || this.POLYGON_BORDER_COLOR;
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  drawPolygon(): void {
    if (this.polygonPoints.length < 2) return;
    this.ctx.beginPath();
    this.ctx.moveTo(this.polygonPoints[0].x, this.polygonPoints[0].y);
    for (let i = 1; i < this.polygonPoints.length; i++) {
      this.ctx.lineTo(this.polygonPoints[i].x, this.polygonPoints[i].y);
    }
    if (this.polygonPoints.length >= 3 && !this.isDrawing) {
      this.ctx.closePath();
      this.ctx.fillStyle = this.POLYGON_FILL_COLOR;
      this.ctx.fill();
    }
    this.ctx.strokeStyle = this.POLYGON_BORDER_COLOR;
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
  }

  drawVertices(): void {
    this.polygonPoints.forEach((point, index) => {
      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, this.VERTEX_RADIUS, 0, 2 * Math.PI);
      this.ctx.fillStyle = this.VERTEX_COLOR;
      this.ctx.fill();
      this.ctx.strokeStyle = '#FFFFFF';
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    });
  }

  startNewZone(): void {
    if (this.polygonPoints.length >= 3) {
      this.saveCurrentZone();
    }
    this.polygonPoints = [];
    this.zoneName = '';
    this.isDrawing = false;
    this.isEditing = false;
    this.selectedVertexIndex = -1;
    this.selectedZoneId = null;
    this.currentZone = null;
    this.drawCanvas();
  }

  saveState(): void {
    const zonesCopy = JSON.parse(JSON.stringify(this.zones));
    this.undoStack.push(zonesCopy);
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
    this.redoStack = [];
  }

  undo(): void {
    if (this.undoStack.length === 0) {
      this.utilService.showErrorInfo('Nothing to undo');
      return;
    }
    const currentState = JSON.parse(JSON.stringify(this.zones));
    this.redoStack.push(currentState);
    const previousState = this.undoStack.pop();
    if (previousState) {
      this.zones = JSON.parse(JSON.stringify(previousState));
      this.polygonPoints = [];
      this.isDrawing = false;
      this.isEditing = false;
      this.selectedVertexIndex = -1;
      this.selectedZoneId = null;
      this.currentZone = null;
      this.drawCanvas();
    }
  }

  redo(): void {
    if (this.redoStack.length === 0) {
      this.utilService.showErrorInfo('Nothing to redo');
      return;
    }
    const currentState = JSON.parse(JSON.stringify(this.zones));
    this.undoStack.push(currentState);
    const nextState = this.redoStack.pop();
    if (nextState) {
      this.zones = JSON.parse(JSON.stringify(nextState));
      this.polygonPoints = [];
      this.isDrawing = false;
      this.isEditing = false;
      this.selectedVertexIndex = -1;
      this.selectedZoneId = null;
      this.currentZone = null;
      this.drawCanvas();
    }
  }

  saveCurrentZone(): void {
    if (this.polygonPoints.length < 3) return;
    this.saveState();
    const normalizedPoints = this.getNormalizedCoordinates();
    const zoneColor = this.POLYGON_BORDER_COLOR;
    if (this.selectedZoneId) {
      const index = this.zones.findIndex(z => z.id === this.selectedZoneId);
      if (index >= 0) {
        this.zones[index] = {
          id: this.selectedZoneId,
          name: `Zone ${index + 1}`,
          coordinates: normalizedPoints,
          color: zoneColor
        };
      }
    } else {
      const newZone: Zone = {
        id: `zone_${Date.now()}`,
        name: `Zone ${this.zones.length + 1}`,
        coordinates: normalizedPoints,
        color: zoneColor
      };
      this.zones.push(newZone);
    }

    this.polygonPoints = [];
    this.zoneName = '';
    this.isDrawing = false;
    this.isEditing = false;
    this.selectedVertexIndex = -1;
    this.selectedZoneId = null;
    this.currentZone = null;
    this.drawCanvas();
  }

  deleteVertex(): void {
    if (this.selectedVertexIndex >= 0 && this.polygonPoints.length > 3) {
      this.polygonPoints.splice(this.selectedVertexIndex, 1);
      this.selectedVertexIndex = -1;
      this.drawCanvas();
    }
  }

  clearCurrentZone(): void {
    this.polygonPoints = [];
    this.isDrawing = false;
    this.isEditing = false;
    this.selectedVertexIndex = -1;
    this.selectedZoneId = null;
    this.currentZone = null;
    this.drawCanvas();
  }

  clearAllZones(): void {
    this.saveState();

    this.zones = [];
    this.polygonPoints = [];
    this.isDrawing = false;
    this.isEditing = false;
    this.selectedVertexIndex = -1;
    this.selectedZoneId = null;
    this.currentZone = null;
    this.drawCanvas();
    this.utilService.showErrorSuccess('All zones cleared successfully!');
  }

  getNormalizedCoordinates(): Point[] {
    return this.polygonPoints.map(point => ({
      x: point.x / this.canvasWidth,
      y: point.y / this.canvasHeight
    }));
  }

  selectZone(zoneId: string): void {
    const zone = this.zones.find(z => z.id === zoneId);
    if (zone) {
      this.selectedZoneId = zoneId;
      this.currentZone = zone;
      this.polygonPoints = zone.coordinates.map(p => ({
        x: p.x * this.canvasWidth,
        y: p.y * this.canvasHeight
      }));
      this.isDrawing = false;
      this.isEditing = false;
      this.drawCanvas();
    }
  }

  deleteZone(zoneId: string): void {
    this.saveState();
    const index = this.zones.findIndex(z => z.id === zoneId);
    if (index >= 0) {
      this.zones.splice(index, 1);
      if (this.selectedZoneId === zoneId) {
        this.selectedZoneId = null;
        this.currentZone = null;
        this.polygonPoints = [];
        this.zoneName = '';
      }
      this.drawCanvas();
    }
  }

  editZone(zoneId: string): void {
    this.selectZone(zoneId);
    this.isEditing = true;
  }

  saveZone(): void {
    if (this.polygonPoints.length < 3) {
      this.utilService.showErrorMessage('Please create a zone with at least 3 points');
      return;
    }

    this.saveCurrentZone();
  }

  saveAllZones(): void {
    const zonesData = {
      zones: this.zones.map(zone => ({
        id: zone.id,
        name: zone.name,
        coordinates: zone.coordinates,
      }))
    };
    const apiPayload = {
      camera: this.siteService.cameraSiteID._id,
      site: this.siteService.cameraSiteID.site,
      zones: zonesData.zones
    };
    this.saveZonesSubscription = this.siteService.updateZoneDetection(apiPayload).subscribe({
      next: (res) => {
        if (res) {
          this.utilService.showErrorSuccess('Zones saved successfully!');
          this.closeModal();
        } else {
          this.utilService.showErrorInfo('Something wrong!');
        }
      },
      error: (e) => { this.utilService.showErrorMessage(e.error?.message || 'Failed to save zones. Please try again.'); },
      complete: () => this.saveZonesSubscription.unsubscribe()
    });

  }

  fetchZones(): void {
    if (!this.siteService.cameraSiteID || !this.siteService.cameraSiteID._id || !this.siteService.cameraSiteID.site) {
      return;
    }
    const params = {
      site: this.siteService.cameraSiteID.site,
      camera: this.siteService.cameraSiteID._id
    };
    this.siteService.getZoneDetection(params).subscribe({
      next: (res: any) => {
        if (res && res.result && res.result.length > 0) {
          const zoneData = res.result[0];
          if (zoneData.zones && Array.isArray(zoneData.zones)) {
            this.zones = zoneData.zones.map((zone: any) => ({
              id: zone.id,
              name: zone.name,
              coordinates: zone.coordinates,
              color: this.POLYGON_BORDER_COLOR
            }));
            this.saveState();
            if (this.ctx && this.imageElement) {
              this.drawCanvas();
            }
          }
        } else {
          this.saveState();
        }
      },
      error: (error) => {
        this.saveState();
      }
    });
  }

  trackByZoneId(index: number, zone: Zone): string {
    return zone.id;
  }

  closeModal(): void {
    if (this.embedded) {
      this.siteService.closeZoneDrawer();
      return;
    }
    this.modalRef?.hide();
  }

  ngOnDestroy(): void {
    if (this.saveZonesSubscription) {
      this.saveZonesSubscription.unsubscribe();
    }
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
    }
  }
}